import React, { useEffect, useState } from "react";
import { api, formatErr } from "../../../lib/api";
// Sibling sections that visually live inside this tab. Both are extracted
// as their own modules — no circular dependency on the shell.
import HolidaysSection from "./HolidaysSection";
import HoursOverridesSection from "./HoursOverridesSection";

/**
 * Working hours tab — weekly recurring open/close schedule with optional
 * second daily window (lunch break / split shift).
 *
 * Extracted from BusinessDashboardShell.jsx (iter28 refactor step 2.5).
 * No logic, UX or API change — only the file location.
 *
 * Capability: gated by `hours:manage` at the shell level. Backend enforces
 * `hours:manage` on PUT /api/businesses/{bid}/hours.
 */

// Local-only helpers — `DAYS`, `min2hm`, `hm2min` are private to this tab.
const DAYS = [
  { k: "0", label: "Lunedì" },
  { k: "1", label: "Martedì" },
  { k: "2", label: "Mercoledì" },
  { k: "3", label: "Giovedì" },
  { k: "4", label: "Venerdì" },
  { k: "5", label: "Sabato" },
  { k: "6", label: "Domenica" },
];

function min2hm(m) {
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return `${String(h).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
}
function hm2min(s) {
  const [h, m] = (s || "00:00").split(":").map(Number);
  return (h || 0) * 60 + (m || 0);
}

export default function WorkingHours({ businessId }) {
  const [hours, setHours] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get(`/businesses/${businessId}/hours`).then((r) => {
      const h = r.data?.hours || {};
      const full = {};
      DAYS.forEach((d) => {
        const cur = h[d.k] || { open: 9 * 60, close: 18 * 60, active: Number(d.k) < 5 };
        full[d.k] = {
          open: cur.open ?? 9 * 60,
          close: cur.close ?? 18 * 60,
          active: !!cur.active,
          open2: cur.open2 ?? null,
          close2: cur.close2 ?? null,
        };
      });
      setHours(full);
    });
  }, [businessId]);

  function update(dayK, patch) {
    setHours((prev) => ({ ...prev, [dayK]: { ...prev[dayK], ...patch } }));
  }

  function applyToAll(dayK) {
    const src = hours[dayK];
    const next = {};
    DAYS.forEach((d) => { next[d.k] = { ...src }; });
    setHours(next);
  }

  async function save() {
    setSaving(true);
    setErr("");
    try {
      await api.put(`/businesses/${businessId}/hours`, { hours });
      setSavedAt(new Date());
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail) || "Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  }

  if (!hours) return <div className="text-sm text-zinc-500 dark:text-zinc-400">Caricamento…</div>;

  return (
    <div className="max-w-3xl">
      <div className="flex items-start md:items-center justify-between flex-col md:flex-row gap-3 mb-4">
        <div>
          <h3 className="font-display text-xl font-medium">Orari di apertura</h3>
          <p className="text-sm text-zinc-500 dark:text-zinc-400">Definisci gli orari per ogni giorno. I clienti potranno prenotare solo in queste fasce.</p>
        </div>
        <div className="flex items-center gap-3">
          {savedAt && <span className="text-xs text-emerald-600" data-testid="hours-saved">Salvato ✓</span>}
          <button onClick={save} disabled={saving} className="btn-accent h-11 text-sm" data-testid="hours-save">
            {saving ? "Salvataggio…" : "Salva orari"}
          </button>
        </div>
      </div>

      {err && <div className="text-sm text-red-600 mb-3">{err}</div>}

      <div className="card-soft divide-y divide-zinc-100 dark:divide-zinc-800">
        {DAYS.map((d) => {
          const h = hours[d.k];
          const hasBreak = h.open2 != null && h.close2 != null;
          return (
            <div key={d.k} className="p-4 md:p-5 flex items-center gap-4 flex-wrap" data-testid={`hours-row-${d.k}`}>
              <div className="w-24 md:w-28 font-medium">{d.label}</div>
              <label className="inline-flex items-center gap-2 text-sm select-none cursor-pointer">
                <input
                  type="checkbox"
                  checked={h.active}
                  onChange={(e) => update(d.k, { active: e.target.checked })}
                  className="h-4 w-4 accent-blue-600"
                  data-testid={`hours-active-${d.k}`}
                />
                <span className={h.active ? "text-zinc-900" : "text-zinc-400"}>
                  {h.active ? "Aperto" : "Chiuso"}
                </span>
              </label>
              <div className={`flex items-center gap-2 ml-auto flex-wrap transition-opacity ${h.active ? "opacity-100" : "opacity-40 pointer-events-none"}`}>
                <input type="time" value={min2hm(h.open)} onChange={(e) => update(d.k, { open: hm2min(e.target.value) })} className="h-11 rounded-xl bg-zinc-50 border border-transparent focus:bg-white focus:border-blue-600 focus:ring-4 focus:ring-blue-600/10 px-3 text-sm outline-none dark:bg-zinc-800" data-testid={`hours-open-${d.k}`} />
                <span className="text-zinc-400 text-sm dark:text-zinc-500">—</span>
                <input type="time" value={min2hm(h.close)} onChange={(e) => update(d.k, { close: hm2min(e.target.value) })} className="h-11 rounded-xl bg-zinc-50 border border-transparent focus:bg-white focus:border-blue-600 focus:ring-4 focus:ring-blue-600/10 px-3 text-sm outline-none dark:bg-zinc-800" data-testid={`hours-close-${d.k}`} />
                {hasBreak ? (
                  <>
                    <span className="text-zinc-400 text-sm px-1 dark:text-zinc-500">+</span>
                    <input type="time" value={min2hm(h.open2)} onChange={(e) => update(d.k, { open2: hm2min(e.target.value) })} className="h-11 rounded-xl bg-zinc-50 border border-transparent focus:bg-white focus:border-blue-600 focus:ring-4 focus:ring-blue-600/10 px-3 text-sm outline-none dark:bg-zinc-800" data-testid={`hours-open2-${d.k}`} />
                    <span className="text-zinc-400 text-sm dark:text-zinc-500">—</span>
                    <input type="time" value={min2hm(h.close2)} onChange={(e) => update(d.k, { close2: hm2min(e.target.value) })} className="h-11 rounded-xl bg-zinc-50 border border-transparent focus:bg-white focus:border-blue-600 focus:ring-4 focus:ring-blue-600/10 px-3 text-sm outline-none dark:bg-zinc-800" data-testid={`hours-close2-${d.k}`} />
                    <button type="button" onClick={() => update(d.k, { open2: null, close2: null })} className="text-xs text-zinc-500 hover:text-red-600 px-2 dark:text-zinc-400" title="Rimuovi seconda fascia" data-testid={`hours-remove2-${d.k}`}>×</button>
                  </>
                ) : (
                  <button type="button" onClick={() => update(d.k, { open2: 15 * 60, close2: 19 * 60 })} className="text-xs text-zinc-500 hover:text-zinc-900 border border-dashed border-zinc-300 hover:border-zinc-500 rounded-full h-9 px-3 dark:text-zinc-400 dark:hover:text-zinc-100 dark:border-zinc-600" data-testid={`hours-add2-${d.k}`}>
                    + Pausa / 2ª fascia
                  </button>
                )}
                <button type="button" onClick={() => applyToAll(d.k)} className="text-xs text-zinc-500 hover:text-zinc-900 px-2 dark:text-zinc-400 dark:hover:text-zinc-100" title="Applica a tutti i giorni" data-testid={`hours-apply-all-${d.k}`}>≡ Tutti</button>
              </div>
            </div>
          );
        })}
      </div>

      <p className="text-xs text-zinc-500 mt-4 dark:text-zinc-400">
        Suggerimento: la griglia degli slot è di 15 minuti. Se un servizio dura 60 min e la chiusura è alle 18:00, l'ultimo slot partirà alle 17:00.
      </p>

      <HolidaysSection businessId={businessId} />
      <HoursOverridesSection businessId={businessId} />
    </div>
  );
}
